import React, { useEffect, useMemo, useRef, useState } from "react";

export type LoadingSize = "sm" | "md" | "lg" | "xl";

type DataAttributes = { [key: `data-${string}`]: string | undefined };

type LoadingProps = DataAttributes & {
  message?: string;
  fullscreen?: boolean;
  overlay?: boolean;
  size?: LoadingSize;
  className?: string;
  minDurationMs?: number;
  visible?: boolean;
};

const sizeMap: Record<LoadingSize, string> = {
  sm: "h-6 w-6 border-2",
  md: "h-10 w-10 border-3",
  lg: "h-14 w-14 border-4",
  xl: "h-20 w-20 border-[6px]",
};

// Hook to gate a loading boolean for a minimum duration
export function useMinimumLoader(isLoading: boolean, minDurationMs = 300) {
  const [gated, setGated] = useState<boolean>(isLoading);
  const startRef = useRef<number | null>(isLoading ? Date.now() : null);

  useEffect(() => {
    // When loading starts, record start time and show immediately
    if (isLoading) {
      startRef.current = Date.now();
      setGated(true);
      return;
    }
    // When loading ends, enforce minimum duration
    const startedAt = startRef.current;
    if (!startedAt) {
      setGated(false);
      return;
    }
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, minDurationMs - elapsed);
    const id = window.setTimeout(() => setGated(false), remaining);
    return () => window.clearTimeout(id);
  }, [isLoading, minDurationMs]);

  return gated;
}

const Loading: React.FC<LoadingProps> = ({
  message = "Loading...",
  fullscreen = false,
  overlay = false,
  size = "md",
  className = "",
  minDurationMs = 0,
  visible = true,
  ...rest
}) => {
  // Optional minimum duration to prevent quick flash when mounting directly
  const [mountedAt] = useState<number>(() => Date.now());
  const [allowHide, setAllowHide] = useState<boolean>(minDurationMs <= 0);

  useEffect(() => {
    if (minDurationMs && minDurationMs > 0) {
      const elapsed = Date.now() - mountedAt;
      const remaining = Math.max(0, minDurationMs - elapsed);
      const id = window.setTimeout(() => setAllowHide(true), remaining);
      return () => window.clearTimeout(id);
    }
  }, [minDurationMs, mountedAt]);

  const spinner = (
    <div className="flex flex-col items-center gap-4">
      <div
        className={`animate-spin rounded-full border-t-transparent ${sizeMap[size]} ${
          // themed ring: purple accent on dark slate background
          "border-purple-500/80 dark:border-purple-400/80"
        } ${className}`}
        role="status"
        aria-live="polite"
        aria-busy="true"
        {...rest}
      />
      {message && (
        <div className="text-sm font-medium text-slate-600 dark:text-slate-300">
          {message}
        </div>
      )}
    </div>
  );

  const containerClass = useMemo(() => {
    if (fullscreen) return "fixed inset-0 z-[9990] flex items-center justify-center";
    if (overlay) return "absolute inset-0 z-[50] flex items-center justify-center";
    return "w-full flex items-center justify-center py-8";
  }, [fullscreen, overlay]);

  const backdrop = useMemo(() => {
    if (fullscreen) return <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" />;
    if (overlay) return <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]" />;
    return null;
  }, [fullscreen, overlay]);

  // If not visible at all, do not render (after all hooks, so hook order is stable)
  if (!visible) return null;

  return (
    <div className={containerClass} style={fullscreen || overlay ? undefined : { minHeight: 80 }}>
      {backdrop}
      <div className="relative z-[51]">{spinner}</div>
      {!allowHide && <div aria-hidden className="sr-only" />}
    </div>
  );
};

export default Loading;
